import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Checkbox,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Chip,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import moment from 'moment'
import EditIcon from '@mui/icons-material/Edit'
import { IFlightTrackerDealer } from '../../types'
import {
  useDeleteFlightTrackerDealersMutation,
  useUpdateFlightTrackerDealerStatusMutation,
} from '../../redux/api/flightTrackerDealerApi'

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
    marginBottom: '20px',
  },
  actionButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    textTransform: 'none',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: '#115293',
    },
  },
  modalButton: {
    textTransform: 'none',
    textDecoration: 'none',
    backgroundColor: '#f1f1f1',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
}

// Helper functions
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1
  if (b[orderBy] > a[orderBy]) return 1
  return 0
}

type Order = 'asc' | 'desc'

function getComparator<Key extends keyof IFlightTrackerDealer>(order: Order, orderBy: Key) {
  return order === 'desc'
    ? (a: IFlightTrackerDealer, b: IFlightTrackerDealer) => descendingComparator(a, b, orderBy)
    : (a: IFlightTrackerDealer, b: IFlightTrackerDealer) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

// Head cells
interface HeadCell {
  id: keyof IFlightTrackerDealer
  label: string
}

const headCells: readonly HeadCell[] = [
  { id: 'flightTrackerStatusDealerId', label: 'ID' },
  { id: 'isActiveFlg', label: 'Active Status' },
  { id: 'flightTrackerStatusDealer', label: 'Dealer Name' },
  { id: 'createdOn', label: 'Created' },
  { id: 'createdBy', label: 'Created By' },
  { id: 'updatedOn', label: 'Updated' },
  { id: 'updatedBy', label: 'Updated By' },
]

interface DealerTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IFlightTrackerDealer) => void
  order: Order
  orderBy: string
  numSelected: number
  rowCount: number
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function DealerTableHead(props: DealerTableHeadProps) {
  const { order, orderBy, onRequestSort, numSelected, rowCount, onSelectAll } = props
  const createSortHandler =
    (property: keyof IFlightTrackerDealer) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: '#1976d2' }}>
        <TableCell padding='checkbox'>
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAll}
            inputProps={{ 'aria-label': 'select all displayed rows' }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={`'${headCell.id}'`}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ color: 'white' }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell padding='checkbox'></TableCell>
      </TableRow>
    </TableHead>
  )
}

interface Props {
  isLoading: boolean
  rows: IFlightTrackerDealer[]
  searchKey: string
  editDealer: (data: IFlightTrackerDealer) => void
  refetch: () => void
}

export const DealerPortalTable = ({ isLoading, rows, searchKey, editDealer, refetch }: Props) => {
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<keyof IFlightTrackerDealer>('isActiveFlg')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [selected, setSelected] = useState<number[]>([])
  const [open, setOpen] = useState(false)
  const [action, setAction] = useState<string>('')

  const [
    deleteDealer,
    { isLoading: delIsLoading, isError: delIsError, error: delError, isSuccess: delIsSuccess },
  ] = useDeleteFlightTrackerDealersMutation()
  const [
    updateDealerStatus,
    {
      isLoading: statusIsLoading,
      isError: statusIsError,
      error: statusError,
      isSuccess: statusIsSuccess,
    },
  ] = useUpdateFlightTrackerDealerStatusMutation()

  const actionIsLoading = useMemo(
    () => delIsLoading || statusIsLoading,
    [delIsLoading, statusIsLoading],
  )

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof IFlightTrackerDealer,
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSelect = (id: number) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id],
    )
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(displayedRows.map((row) => Number(row.flightTrackerStatusDealerId)))
    } else {
      setSelected([])
    }
  }

  const handleAction = (actionType: string) => {
    setAction(actionType)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setAction('')
  }

  const handleConfirm = async () => {
    try {
      switch (action) {
        case 'activate':
          await updateDealerStatus({ dealerIds: selected, isActiveFlg: 1 })
          break
        case 'deactivate':
          await updateDealerStatus({ dealerIds: selected, isActiveFlg: 0 })
          break
        case 'delete':
          await deleteDealer({ dealerIds: selected, isActiveFlg: 0 })
          break
        default:
          break
      }
      setSelected([])
      handleClose()
      refetch()
    } catch (error) {
      console.error('Error performing action:', error)
    }
  }

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) => value && value.toString().toLowerCase().includes(searchKey.toLowerCase()),
    ),
  )

  const sortedRows = stableSort(filteredRows, getComparator(order, orderBy))
  const displayedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const emptyRows = rowsPerPage - displayedRows.length

  return (
    <>
      <Box sx={styles.actionsContainer}>
        <Button
          variant='contained'
          sx={styles.actionButton}
          onClick={() => handleAction('activate')}
          disabled={selected.length === 0 || actionIsLoading}
        >
          Activate
          {actionIsLoading && action === 'activate' && (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          )}
        </Button>
        <Button
          variant='contained'
          sx={styles.actionButton}
          onClick={() => handleAction('deactivate')}
          disabled={selected.length === 0 || actionIsLoading}
        >
          Deactivate
          {actionIsLoading && action === 'deactivate' && (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          )}
        </Button>
        <Button
          variant='contained'
          sx={styles.actionButton}
          onClick={() => handleAction('delete')}
          disabled={selected.length === 0 || actionIsLoading}
        >
          Delete
          {actionIsLoading && action === 'delete' && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Button>
      </Box>
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {action} the selected dealers?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={styles.modalButton}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} sx={styles.modalButton}>
            Confirm
            {actionIsLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby='tableTitle' size='medium'>
            <DealerTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              numSelected={selected.length}
              rowCount={displayedRows.length}
              onSelectAll={handleSelectAll}
            />
            <TableBody>
              {displayedRows.map((row) => {
                const isItemSelected = selected.includes(Number(row.flightTrackerStatusDealerId))
                return (
                  <TableRow
                    hover
                    onClick={() => handleSelect(Number(row.flightTrackerStatusDealerId))}
                    role='checkbox'
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.flightTrackerStatusDealerId}
                    selected={isItemSelected}
                  >
                    <TableCell padding='checkbox'>
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>{row.flightTrackerStatusDealerId}</TableCell>
                    <TableCell>
                      {row?.isActiveFlg ? (
                        <Chip label='Active' color='primary' size='small' />
                      ) : (
                        <Chip label='Deactive' color='error' size='small' />
                      )}
                    </TableCell>
                    <TableCell>{row.flightTrackerStatusDealer}</TableCell>
                    <TableCell>{moment(row?.createdOn).format('L')}</TableCell>
                    <TableCell>{row.createdBy}</TableCell>
                    <TableCell>{moment(row?.updatedOn).format('L')}</TableCell>
                    <TableCell>{row.updatedBy}</TableCell>
                    <TableCell padding='checkbox'>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          editDealer(row)
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
              {/* {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )} */}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  )
}
